/**
 * Stores > Today at /stores/today.
 *
 * The store manager's daily routine, mobile-first (built for a phone). Three
 * phase cards (Opening / Midday / Closing) each show submitted-vs-pending state
 * for today, and open a phase form: traffic count (pre-filled from the auto
 * feed when present, editable), transactions (closing only), the phase
 * checklist, a note, and photo capture.
 *
 * Gated by stores:store. An unmapped store user (403 'unmapped') sees the
 * "contact HQ" state. A 404/503 renders the not-available placeholder.
 */

import { useState } from 'react';
import { Check, Clock, Camera, ChevronRight } from 'lucide-react';
import { ToolPinToggle } from '@/components/ToolPinToggle';
import { useSession } from '@/lib/useSession';
import { formatDate } from '@/lib/dateUtils';
import {
  useStoreMe,
  useStoreReports,
  useScorecard,
  hasStoresStore,
  hasStoresHq,
  isNotAvailable,
  isUnmapped,
  denverToday,
  PHASE_LABELS,
  STORE_PHASES,
  type StorePhase,
  type PhaseState,
  storeLabel,
} from '@/lib/useStores';
import {
  StoresHeader,
  AccessDeniedCard,
  UnmappedCard,
  NotAvailableCard,
  ErrorCard,
} from './storesShared';
import { PhaseForm } from './PhaseForm';

export function Today() {
  const { user } = useSession();
  const canView = hasStoresStore(user?.permissions, user?.superAdmin);
  const isHq = hasStoresHq(user?.permissions, user?.superAdmin);

  // An HQ user has no mapped store; they pick one and every store-scoped call
  // carries ?store_no=. Store users leave this null and act on their own store.
  const [hqStoreNo, setHqStoreNo] = useState<number | null>(null);
  const scopedStoreNo = isHq && hqStoreNo !== null ? hqStoreNo : undefined;

  const meQ = useStoreMe(scopedStoreNo);
  const date = denverToday();
  const reportsQ = useStoreReports(date, scopedStoreNo);

  const [openPhase, setOpenPhase] = useState<StorePhase | null>(null);

  if (user && !canView) return <AccessDeniedCard what="The daily routine" />;

  // The scope errors surface on either query. Unmapped is the friendly state:
  // HQ users get a store picker (they act on any store), store users get the
  // "contact HQ to get linked" card.
  if (isUnmapped(meQ.error) || isUnmapped(reportsQ.error)) {
    return (
      <div className="mx-auto w-full max-w-lg">
        <StoresHeader title="Today" actions={<ToolPinToggle toolSlug="stores-today" toolName="Today" />} />
        {isHq ? (
          <HqStorePickerCard date={date} value={hqStoreNo} onChange={setHqStoreNo} />
        ) : (
          <UnmappedCard />
        )}
      </div>
    );
  }

  if (isNotAvailable(meQ.error) || isNotAvailable(reportsQ.error)) {
    return (
      <div className="mx-auto w-full max-w-lg">
        <StoresHeader title="Today" actions={<ToolPinToggle toolSlug="stores-today" toolName="Today" />} />
        <NotAvailableCard what="The daily routine" />
      </div>
    );
  }

  const me = meQ.data;
  const reports = reportsQ.data;
  const storeName = me?.store?.store_name;

  return (
    <div className="mx-auto w-full max-w-lg pb-24">
      <StoresHeader
        title="Today"
        subtitle={
          storeName
            ? `${storeName} · ${formatDate(date)}`
            : formatDate(date)
        }
        actions={<ToolPinToggle toolSlug="stores-today" toolName="Today" />}
      />

      {isHq && (
        <div className="mb-4">
          <HqStoreSelect date={date} value={hqStoreNo} onChange={setHqStoreNo} />
        </div>
      )}

      {meQ.isLoading || reportsQ.isLoading ? (
        <div className="space-y-3">
          {STORE_PHASES.map((p) => (
            <div key={p} className="h-24 animate-pulse rounded-xl bg-amp-cream dark:bg-amp-dark-surface-warm" />
          ))}
        </div>
      ) : reportsQ.error ? (
        <ErrorCard message={reportsQ.error.message} />
      ) : (
        <div className="space-y-3">
          {STORE_PHASES.map((phase) => (
            <PhaseCard
              key={phase}
              phase={phase}
              state={reports?.phases[phase]}
              onOpen={() => setOpenPhase(phase)}
            />
          ))}
        </div>
      )}

      {openPhase && me && reports && (
        <PhaseForm
          phase={openPhase}
          date={date}
          me={me}
          state={reports.phases[openPhase]}
          trafficAuto={reports.traffic_auto}
          onClose={() => setOpenPhase(null)}
        />
      )}
    </div>
  );
}

// HQ store selection. The daily routine is store-scoped; an HQ user has no
// mapped store, so they pick which store to view (the API accepts ?store_no=
// from stores:hq holders). The list reuses the scorecard's active-store rows,
// the same source the broadcast custom picker uses. Exported for reuse by the
// Inventory Count / variance / variance history pages (same HQ
// acts-on-a-store pattern; see InventoryStoreHeader.tsx).
//
// `allowAll`, when set, adds an "All stores" option that calls onChange(null)
// — used only by Variance history, the one inventory page where "every
// store" is itself a valid view.
export function HqStoreSelect({
  date,
  value,
  onChange,
  allowAll = false,
}: {
  date: string;
  value: number | null;
  onChange: (storeNo: number | null) => void;
  allowAll?: boolean;
}) {
  const scoreQ = useScorecard(date);
  const stores = [...(scoreQ.data?.stores ?? [])].sort((a, b) => a.store_no - b.store_no);

  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-amp-ink-muted dark:text-amp-dark-ink-muted">
        Viewing store
      </span>
      <select
        value={value ?? ''}
        onChange={(e) => {
          if (e.target.value === '') {
            if (allowAll) onChange(null);
            return;
          }
          const v = Number(e.target.value);
          if (Number.isFinite(v)) onChange(v);
        }}
        className="w-full rounded-md border border-amp-line bg-amp-white px-3 py-2 text-sm text-amp-ink focus:border-amp-olive focus:outline-none dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
      >
        {allowAll ? (
          <option value="">All stores</option>
        ) : (
          <option value="" disabled>
            {scoreQ.isLoading ? 'Loading stores…' : 'Choose a store…'}
          </option>
        )}
        {stores.map((s) => (
          <option key={s.store_no} value={s.store_no}>
            {storeLabel(s.store_no, s.store_name)}
          </option>
        ))}
      </select>
    </label>
  );
}

// First-visit state for an HQ user: no store picked yet, so the store-scoped
// queries 403 (unmapped). Rendered in place of the phase cards.
function HqStorePickerCard({
  date,
  value,
  onChange,
}: {
  date: string;
  value: number | null;
  onChange: (storeNo: number | null) => void;
}) {
  return (
    <div className="rounded-lg border border-amp-line bg-amp-white p-6 dark:border-amp-dark-line dark:bg-amp-dark-surface">
      <h2 className="mb-1 text-lg font-semibold text-amp-ink dark:text-amp-dark-ink">
        Pick a store to view
      </h2>
      <p className="mb-4 text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
        Your HQ account isn't tied to a single store. Choose a store to see and
        submit its daily routine.
      </p>
      <HqStoreSelect date={date} value={value} onChange={onChange} />
    </div>
  );
}

function PhaseCard({
  phase,
  state,
  onOpen,
}: {
  phase: StorePhase;
  state: PhaseState | undefined;
  onOpen: () => void;
}) {
  const submitted = !!state?.submitted;
  const photoCount = state?.report?.photos?.length ?? 0;
  const traffic = state?.report?.traffic_count;
  const transactions = state?.report?.transactions;

  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex w-full items-center gap-3 rounded-xl border border-amp-line bg-amp-white px-4 py-4 text-left shadow-1 hover:bg-amp-cream-warm dark:border-amp-dark-line dark:bg-amp-dark-surface dark:hover:bg-amp-dark-surface-warm"
    >
      <span
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
          submitted
            ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
            : 'bg-amp-cream-warm text-amp-ink-muted dark:bg-amp-dark-surface-warm dark:text-amp-dark-ink-muted'
        }`}
        aria-hidden
      >
        {submitted ? <Check className="h-5 w-5" /> : <Clock className="h-5 w-5" />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[16px] font-semibold text-amp-ink dark:text-amp-dark-ink">
          {PHASE_LABELS[phase]}
        </span>
        <span className="block text-[13px] text-amp-ink-muted dark:text-amp-dark-ink-muted">
          {submitted ? (
            <>
              Submitted
              {traffic != null ? ` · ${traffic.toLocaleString()} visitors` : ''}
              {transactions != null ? ` · ${transactions.toLocaleString()} transactions` : ''}
              {photoCount > 0 ? (
                <>
                  {' · '}
                  <span className="inline-flex items-center gap-0.5">
                    <Camera className="h-3 w-3" aria-hidden /> {photoCount}
                  </span>
                </>
              ) : (
                ''
              )}
            </>
          ) : (
            'Not submitted yet. Tap to fill in.'
          )}
        </span>
      </span>
      <ChevronRight className="h-5 w-5 shrink-0 text-amp-ink-muted dark:text-amp-dark-ink-muted" aria-hidden />
    </button>
  );
}
